<template>
  <div
    v-if="availableLanguages.length > 1"
    ref="wrapper"
    class="relative">
    <button
      type="button"
      :aria-label="$t('W: Vælg sprog')"
      aria-haspopup="listbox"
      :aria-expanded="isOpen"
      class="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-border-subtle bg-surface px-2.5 py-1 text-text-primary hover:bg-surface-muted"
      @click="isOpen = !isOpen">
      <img
        v-if="currentLanguage"
        :src="`https://hatscripts.github.io/circle-flags/flags/${localeCountryCode(currentLocale)}.svg`"
        width="18"
        height="18"
        :alt="currentLanguage.name"
        class="block shrink-0 rounded-full"
        aria-hidden="true" />
      <span class="text-xs font-semibold uppercase leading-none">{{ currentLanguage?.code ?? '' }}</span>
      <IconLucideChevronUp v-if="isOpen" class="h-3.5 w-3.5 shrink-0" />
      <IconLucideChevronDown v-else class="h-3.5 w-3.5 shrink-0" />
    </button>

    <div
      v-if="isOpen"
      role="listbox"
      :aria-label="$t('W: Vælg sprog')"
      class="absolute right-0 top-full z-10 mt-1 min-w-[9rem] rounded-[var(--card-radius)] border border-border-subtle bg-surface py-1 shadow-lg">
      <div
        v-for="language in availableLanguages"
        :key="language.locale"
        role="option"
        :aria-selected="language.locale === currentLocale"
        class="flex cursor-pointer items-center gap-2.5 px-3 py-2 hover:bg-surface-muted"
        @click="onLanguageChange(language)">
        <img
          :src="`https://hatscripts.github.io/circle-flags/flags/${localeCountryCode(language.locale)}.svg`"
          width="18"
          height="18"
          :alt="language.name"
          class="block shrink-0 rounded-full"
          aria-hidden="true" />
        <span
          class="grow text-sm"
          :class="language.locale === currentLocale ? 'font-semibold text-text-primary' : 'text-text-secondary'">
          {{ nativeName(language.locale) }}
        </span>
        <IconLucideCheck
          v-if="language.locale === currentLocale"
          class="h-3.5 w-3.5 shrink-0 text-action" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { i18n, SUPPORTED_LOCALES, type TAppLocale } from '@/locales'
import { useAccountStore } from '@/stores/accountStore'
import { useBrandStore } from '@/stores/brandStore'
import { useUserStore } from '@/stores/userStore'
import { LAB_CUSTOMER_LANGUAGES } from '@team-milva/lab-models/language'
import type { ILabLanguage, TLabCustomerLanguageCode } from '@team-milva/lab-models/language'
import { mapActions, mapState } from 'pinia'
import { sortBy } from 'lodash-es'
import * as Sentry from '@sentry/vue'

const LOCALE_COUNTRY_CODES: Record<string, string> = {
  en: 'gb',
  da: 'dk',
  de: 'de',
  no: 'no',
  fr: 'fr',
}

const LOCALE_NATIVE_NAMES: Record<string, string> = {
  en: 'English',
  da: 'Dansk',
  de: 'Deutsch',
  no: 'Norsk',
  fr: 'Français',
}

export default {
  name: 'LangSelect',
  data() {
    return {
      isOpen: false,
    }
  },
  computed: {
    ...mapState(useAccountStore, {
      accountAvailableLanguages: 'availableLanguages',
      labAccount: 'labAccount',
    }),
    ...mapState(useBrandStore, ['customerLocales']),
    // Post-login, the account-scoped list already correctly narrows to the signed-in
    // user's own lab (and, once a CustomerAccount is loaded, that account's own set —
    // see "Per-Lab & Per-Customer Language Selection", Phase 2). Pre-login, narrow to
    // the resolved brand's customerLocales instead — accountStore has no lab loaded
    // yet to narrow against. An empty customerLocales (no brand configured, or the
    // live fetch hasn't resolved yet) falls back to accountAvailableLanguages' own
    // no-account branch, which is the same full default set as before this existed.
    // Also filtered against SUPPORTED_LOCALES, CA's actual i18n-runtime ceiling — a lab
    // can configure a customerLanguage (e.g. NO/FR) that this build can't render yet.
    availableLanguages(): ILabLanguage[] {
      if (this.labAccount) return this.accountAvailableLanguages
      if (this.customerLocales.length === 0) return this.accountAvailableLanguages

      return sortBy(
        Object.values(LAB_CUSTOMER_LANGUAGES).filter(
          (language) =>
            this.customerLocales.includes(language.locale) &&
            (SUPPORTED_LOCALES as readonly string[]).includes(language.locale)
        ),
        'code'
      )
    },
    currentLocale(): string {
      return i18n.global.locale.value
    },
    currentLanguage() {
      return this.availableLanguages.find((x) => x.locale === this.currentLocale)
    },
  },
  methods: {
    ...mapActions(useUserStore, ['updateLanguage']),
    localeCountryCode(locale: string): string {
      return LOCALE_COUNTRY_CODES[locale] ?? locale
    },
    nativeName(locale: string): string {
      return LOCALE_NATIVE_NAMES[locale] ?? locale.toUpperCase()
    },
    // Applies immediately regardless of login state (localStorage keeps
    // serving as the pre-login/offline fallback). Once logged in, also
    // persists server-side so the choice survives a fresh device/session —
    // best-effort, matching userStore.updateLastSeen's silent-catch pattern,
    // since the visible language switch already is the user's feedback. See
    // "Per-Lab & Per-Customer Language Selection", Phase 3.
    async onLanguageChange(language: ILabLanguage) {
      i18n.global.locale.value = language.locale as TAppLocale
      localStorage.setItem('lang', language.locale)
      this.isOpen = false

      if (this.labAccount) {
        try {
          await this.updateLanguage(language.code as TLabCustomerLanguageCode)
        } catch (error) {
          Sentry.captureException(error)
        }
      }
    },
    onClickOutside(event: MouseEvent) {
      const wrapper = this.$refs.wrapper as HTMLElement | undefined
      if (this.isOpen && wrapper && !wrapper.contains(event.target as Node)) {
        this.isOpen = false
      }
    },
  },
  mounted() {
    document.addEventListener('click', this.onClickOutside)
  },
  unmounted() {
    document.removeEventListener('click', this.onClickOutside)
  },
}
</script>
